import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Shield, Loader2, AlertCircle, CheckCircle2, XCircle, Search, RefreshCw, Trash2, X, Upload, FileSpreadsheet, Eye, Copy, Check } from 'lucide-react';
import AdminNav from './AdminNav';
import AdminGate from './AdminGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProfessionalsInner = ({ adminEmail }) => {
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [resetError, setResetError] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importWipe, setImportWipe] = useState(false);
  const [importRunning, setImportRunning] = useState(false);
  const [importDryRun, setImportDryRun] = useState(null);
  const [importLiveResult, setImportLiveResult] = useState(null);
  const [importError, setImportError] = useState('');
  const [copiedToken, setCopiedToken] = useState('');

  const copyClaimLink = useCallback(async (claimUrl, claimPath, token) => {
    const text = claimUrl || `${window.location.origin}${claimPath || ''}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedToken(token);
      setTimeout(() => setCopiedToken((c) => (c === token ? '' : c)), 2000);
    } catch (e) {
      console.warn('Copy failed', e);
      window.prompt('Copy this claim link:', text);
    }
  }, []);

  const fetchPros = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/professionals`, { withCredentials: true });
      setPros(res.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load professionals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPros(); }, [fetchPros]);

  const [resending, setResending] = useState(null);

  const handleResendInvite = async (proId) => {
    setResending(proId);
    try {
      const res = await axios.post(
        `${API}/admin/professionals/${proId}/resend-invite`,
        {},
        { withCredentials: true }
      );
      const msg = res.data.email_sent
        ? `Invite re-sent to ${res.data.email}`
        : `Token refreshed but email not delivered. Claim URL:\n${res.data.claim_url}`;
      window.alert(msg);
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Failed to resend invite');
    } finally {
      setResending(null);
    }
  };

  const handleToggle = async (proId, currentValue) => {
    setToggling(proId);
    setError('');
    try {
      const res = await axios.put(
        `${API}/admin/professionals/${proId}/toggle-active`,
        { is_active_professional: !currentValue },
        { withCredentials: true }
      );
      setPros(prev => prev.map(p =>
        p.id === proId ? { ...p, is_active_professional: res.data.is_active_professional } : p
      ));
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to toggle status');
    } finally {
      setToggling(null);
    }
  };

  const openResetModal = () => {
    setResetResult(null);
    setResetError('');
    setResetModalOpen(true);
  };

  const closeResetModal = () => {
    if (resetting) return;
    setResetModalOpen(false);
  };

  const runReset = async () => {
    setResetting(true);
    setResetError('');
    setResetResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/reset-test-data`,
        {},
        { withCredentials: true }
      );
      setResetResult(res.data);
      await fetchPros();
    } catch (e) {
      setResetError(e.response?.data?.detail || 'Reset failed.');
    } finally {
      setResetting(false);
    }
  };

  const handleImportDryRun = async () => {
    if (!importFile) { setImportError('Please select an Excel (.xlsx) file first.'); return; }
    setImportError('');
    setImportRunning(true);
    setImportDryRun(null);
    setImportLiveResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await axios.post(`${API}/admin/import-professionals/dry-run`, fd, {
        withCredentials: true,
      });
      setImportDryRun(res.data);
    } catch (e) {
      setImportError(e.response?.data?.detail || 'Dry-run failed.');
    } finally {
      setImportRunning(false);
    }
  };

  const handleImportLive = async () => {
    if (!importFile) { setImportError('Please select an Excel (.xlsx) file first.'); return; }
    if (!importDryRun) { setImportError('Run a dry-run first to validate the file.'); return; }
    const confirmMsg = importWipe
      ? `This will DELETE all ${pros.length} existing professionals (backup saved automatically) and import ${importDryRun.valid_rows} from the file. Continue?`
      : `This will import ${importDryRun.valid_rows} professionals (existing ones are kept). Continue?`;
    if (!window.confirm(confirmMsg)) return;

    setImportError('');
    setImportRunning(true);
    setImportLiveResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('wipe_existing', importWipe ? 'true' : 'false');
      const res = await axios.post(`${API}/admin/import-professionals`, fd, {
        withCredentials: true,
      });
      setImportLiveResult(res.data);
      setImportDryRun(null);
      await fetchPros();
    } catch (e) {
      setImportError(e.response?.data?.detail || 'Import failed.');
    } finally {
      setImportRunning(false);
    }
  };

  const filtered = pros.filter(p => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (p.name || '').toLowerCase().includes(q) ||
      (p.phone || '').toLowerCase().includes(q) ||
      (p.id || '').toLowerCase().includes(q)
    );
  });

  const activeCount = pros.filter(p => p.is_active_professional).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav activeEmail={adminEmail} />
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#37b34a]/10 rounded-xl flex items-center justify-center">
              <Shield size={18} className="text-[#37b34a]" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Professionals</h1>
              <p className="text-xs text-gray-500">Manage active professionals</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchPros()}
              data-testid="admin-refresh-btn"
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500 font-medium">Total Professionals</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{pros.length}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500 font-medium">Active</p>
            <p className="text-2xl font-bold text-[#37b34a] mt-1">{activeCount}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500 font-medium">Pending</p>
            <p className="text-2xl font-bold text-amber-600 mt-1">{pros.length - activeCount}</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              data-testid="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone, or ID"
              className="flex-1 text-sm text-gray-900 placeholder-gray-400 outline-none bg-transparent"
            />
          </div>

          {error && (
            <div className="px-4 py-2 bg-red-50 text-red-700 text-sm flex items-center gap-2 border-b border-red-100">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          {loading && pros.length === 0 ? (
            <div className="py-16 flex items-center justify-center">
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">No professionals found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-pros-table">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Name</th>
                    <th className="text-left px-4 py-3 font-semibold">Phone</th>
                    <th className="text-left px-4 py-3 font-semibold">Areas</th>
                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                    <th className="text-right px-4 py-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{p.name || '—'}</div>
                        <div className="text-xs text-gray-400 font-mono">{p.id?.slice(0, 8)}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{p.phone || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">
                        {typeof p.service_areas === 'string'
                          ? p.service_areas
                          : (p.service_areas?.areas?.join(', ') || '—')}
                      </td>
                      <td className="px-4 py-3">
                        {p.is_active_professional ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-semibold border border-green-200">
                            <CheckCircle2 size={12} /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-200">
                            <XCircle size={12} /> Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!p.claimed_at && (
                            <button
                              onClick={() => handleResendInvite(p.id)}
                              disabled={resending === p.id}
                              data-testid={`admin-resend-invite-${p.id}`}
                              title="Generate a fresh 24-hour invite link and email it"
                              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 transition-colors"
                            >
                              {resending === p.id ? <Loader2 size={12} className="animate-spin" /> : 'Resend invite'}
                            </button>
                          )}
                          <button
                            onClick={() => handleToggle(p.id, p.is_active_professional)}
                            disabled={toggling === p.id}
                            data-testid={`admin-toggle-active-${p.id}`}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                              p.is_active_professional
                                ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                                : 'bg-[#37b34a] text-white hover:bg-[#2d9a3e]'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {toggling === p.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : p.is_active_professional ? (
                              'Deactivate'
                            ) : (
                              'Activate'
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Bulk Import Professionals */}
          <div className="mt-10 bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="admin-import-card">
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
              <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <FileSpreadsheet size={14} /> Bulk Import Professionals
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-xs text-gray-500 leading-relaxed">
                Upload an Excel file with columns: <strong>Business Name, Service Offering, Phone, Email, Address, City, Province, Postal Code</strong>.
                Duplicate emails are auto-suffixed (<code className="bg-gray-100 px-1 rounded">+1, +2, …</code>), phones are normalised to South African format, and a description is auto-generated.
              </p>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <label
                  className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-300 hover:border-[#37b34a] cursor-pointer text-sm text-gray-600 transition-colors"
                  data-testid="import-file-label"
                >
                  <Upload size={14} />
                  <span className="flex-1 truncate">{importFile ? importFile.name : 'Choose .xlsx file…'}</span>
                  <input
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setImportFile(f);
                      setImportDryRun(null);
                      setImportLiveResult(null);
                      setImportError('');
                      e.target.value = '';
                    }}
                    className="hidden"
                    data-testid="import-file-input"
                  />
                </label>

                <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={importWipe}
                    onChange={(e) => setImportWipe(e.target.checked)}
                    data-testid="import-wipe-checkbox"
                    className="accent-red-600"
                  />
                  Wipe existing professionals first (backup saved automatically)
                </label>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleImportDryRun}
                  disabled={!importFile || importRunning}
                  data-testid="import-dry-run-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 transition-colors"
                >
                  {importRunning && !importLiveResult ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                  Run dry-run
                </button>
                <button
                  onClick={handleImportLive}
                  disabled={!importDryRun || importRunning || importDryRun?.valid_rows === 0}
                  data-testid="import-live-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 transition-colors"
                >
                  {importRunning && !!importDryRun ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {importWipe ? 'Wipe & import' : 'Import (additive)'}
                </button>
              </div>

              {importError && (
                <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg" data-testid="import-error">
                  <AlertCircle size={14} />
                  {importError}
                </div>
              )}

              {(importDryRun || importLiveResult) && (() => {
                const d = importLiveResult || importDryRun;
                const isLive = !!importLiveResult;
                return (
                  <div className={`rounded-lg border p-4 ${isLive ? 'bg-green-50 border-green-200' : 'bg-blue-50/40 border-blue-200'}`} data-testid="import-result">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle2 size={16} className={isLive ? 'text-green-700' : 'text-blue-700'} />
                      <p className={`text-sm font-bold ${isLive ? 'text-green-800' : 'text-blue-800'}`}>
                        {isLive ? `Imported ${d.imported} professionals` : `Dry-run: ${d.valid_rows} valid · ${d.invalid_rows} invalid`}
                      </p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-gray-500 uppercase font-semibold mb-1">Categories</p>
                        <ul className="space-y-0.5">
                          {Object.entries(d.categories_imported || {}).map(([slug, n]) => (
                            <li key={slug} className="flex justify-between text-gray-800">
                              <span>{slug}</span>
                              <span className="font-mono font-semibold">{n}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="text-gray-700 space-y-1">
                        <p>Total rows in file: <strong>{d.total_rows_in_file}</strong></p>
                        <p>Valid: <strong>{d.valid_rows}</strong></p>
                        <p>Invalid: <strong>{d.invalid_rows}</strong></p>
                        <p>Location auto-corrections: <strong>{d.location_swap_corrections}</strong></p>
                        {isLive && (
                          <>
                            <p>Deleted existing: <strong>{d.deleted_existing}</strong></p>
                            {d.backup_collection && (
                              <p className="text-xs text-gray-500 break-all">Backup: <code>{d.backup_collection}</code></p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {d.errors && d.errors.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-red-700 font-semibold mb-1">Errors ({d.errors.length}):</p>
                        <ul className="text-xs text-red-600 space-y-0.5 max-h-32 overflow-y-auto">
                          {d.errors.slice(0, 20).map((e, i) => (
                            <li key={i}>Row {e.row_number} — <strong>{e.business_name}</strong>: {e.errors.join(', ')}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {isLive && Array.isArray(d.imported_pros) && d.imported_pros.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-gray-200" data-testid="import-claim-links">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-gray-800">
                            Claim links ({d.imported_pros.length}) — hand-deliver these to your pros
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              const text = d.imported_pros
                                .map((p) => `${p.business_name} (${p.email})\n  ${p.claim_url}`)
                                .join('\n\n');
                              copyClaimLink(text, '', '__bulk__');
                            }}
                            className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                            data-testid="copy-all-claim-links-btn"
                          >
                            {copiedToken === '__bulk__' ? (
                              <><Check size={12} /> Copied all</>
                            ) : (
                              <><Copy size={12} /> Copy all</>
                            )}
                          </button>
                        </div>
                        <ul className="text-xs space-y-1 max-h-72 overflow-y-auto pr-1 bg-white rounded-md border border-gray-200">
                          {d.imported_pros.map((p) => {
                            const isCopied = copiedToken === p.claim_token;
                            const statusBadge =
                              p.invite_status === 'sent'
                                ? <span className="text-[10px] font-semibold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">✉️ sent</span>
                                : p.invite_status === 'failed'
                                  ? <span className="text-[10px] font-semibold text-red-700 bg-red-50 px-1.5 py-0.5 rounded">✉️ failed</span>
                                  : <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">✉️ skipped</span>;
                            return (
                              <li
                                key={p.claim_token}
                                className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                                data-testid={`import-pro-row-${p.id}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-gray-900 truncate">{p.business_name}</span>
                                    {statusBadge}
                                  </div>
                                  <div className="text-gray-500 truncate">{p.email} · {p.service_slug} · {p.city}, {p.province}</div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => copyClaimLink(p.claim_url, p.claim_path, p.claim_token)}
                                  className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-colors ${
                                    isCopied
                                      ? 'bg-emerald-100 text-emerald-800'
                                      : 'bg-emerald-600 text-white hover:bg-emerald-700'
                                  }`}
                                  title={p.claim_url}
                                  data-testid={`copy-claim-link-btn-${p.id}`}
                                >
                                  {isCopied ? (
                                    <><Check size={12} /> Copied</>
                                  ) : (
                                    <><Copy size={12} /> Copy link</>
                                  )}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                        <p className="text-[11px] text-gray-500 mt-2">
                          Tip: WhatsApp / SMS / personal email work fine if Resend hasn't delivered. Links expire at the time on each pro's record.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Danger Zone */}
          <div className="mt-10 bg-white border border-red-200 rounded-2xl overflow-hidden" data-testid="admin-danger-zone">
            <div className="bg-red-50 border-b border-red-200 px-6 py-3">
              <h2 className="text-sm font-bold text-red-700 flex items-center gap-2">
                <Trash2 size={14} /> Danger Zone
              </h2>
            </div>
            <div className="p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900">Reset test data</h3>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Permanently delete all registered profiles (professionals + customers), service requests, lead unlocks,
                  lead reports, credit / payment transactions, and early registrations. Admin audit logs and Stripe
                  webhook records are preserved.
                </p>
              </div>
              <button
                onClick={openResetModal}
                data-testid="open-reset-modal-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors flex-shrink-0"
              >
                <Trash2 size={14} />
                Reset test data
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Reset Test Data Modal */}
      {resetModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={closeResetModal}
          data-testid="reset-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Trash2 size={16} className="text-red-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Reset test data?</h2>
                  <p className="text-xs text-gray-500 mt-0.5">This action cannot be undone.</p>
                </div>
              </div>
              <button
                onClick={closeResetModal}
                disabled={resetting}
                className="p-1 text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-50"
                data-testid="reset-modal-close"
              >
                <X size={18} />
              </button>
            </div>

            {!resetResult ? (
              <>
                <div className="bg-red-50/50 border border-red-100 rounded-lg p-3 mb-4 text-xs text-red-700 space-y-1">
                  <p className="font-semibold">Will be permanently deleted:</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    <li>All professional profiles</li>
                    <li>All customer accounts &amp; sessions</li>
                    <li>All service requests &amp; their photos</li>
                    <li>All lead unlocks, reports, &amp; credit / payment transactions</li>
                    <li>All early registrations</li>
                  </ul>
                  <p className="pt-2 text-red-600 font-medium">
                    Preserved: admin audit logs, Stripe webhook events.
                  </p>
                </div>

                {resetError && (
                  <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg mb-3">
                    <AlertCircle size={14} />
                    {resetError}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={closeResetModal}
                    disabled={resetting}
                    className="flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 transition-colors"
                    data-testid="reset-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={runReset}
                    disabled={resetting}
                    data-testid="reset-confirm-btn"
                    className="flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300 transition-colors flex items-center justify-center gap-2"
                  >
                    {resetting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    Yes, delete everything
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 bg-green-50 text-green-700 text-sm px-3 py-2.5 rounded-lg mb-4" data-testid="reset-success">
                  <CheckCircle2 size={16} />
                  <span className="font-semibold">Deleted {resetResult.total_deleted} records</span>
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg text-xs">
                  <table className="w-full">
                    <tbody>
                      {Object.entries(resetResult.deleted_counts || {}).map(([coll, n]) => (
                        <tr key={coll} className="border-b border-gray-100 last:border-0">
                          <td className="px-3 py-1.5 text-gray-600">{coll}</td>
                          <td className="px-3 py-1.5 text-right font-mono font-semibold text-gray-900">{n}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  onClick={closeResetModal}
                  data-testid="reset-done-btn"
                  className="mt-4 w-full py-2.5 px-4 rounded-lg text-sm font-semibold text-white bg-[#37b34a] hover:bg-[#2d9a3e] transition-colors"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminProfessionals = () => (
  <AdminGate>
    {({ adminEmail }) => <AdminProfessionalsInner adminEmail={adminEmail} />}
  </AdminGate>
);

export default AdminProfessionals;
